import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { backendApi } from "../../../../shared/api/backendApi";
import type { LoginResponse, RegisterRequest, RegisterResponse, VerifyEmailResponse } from "../../../../shared/models/RegisterRequest";
// ==========================================
// ✅ User Registration (useMutation)
// ==========================================
export const useRegisterUser = () => {
  return useMutation<RegisterResponse, Error, RegisterRequest>({
    mutationFn: (data: RegisterRequest) => backendApi.register(data),
  });
};

// ==========================================
// ✅ E-Mail-Verifizierung (useQuery)
// ==========================================
//Verifiziert die E-Mail-Adresse anhand des Tokens
export const useVerifyEmail = (token: string | null) => {
  return useQuery<VerifyEmailResponse, Error>({
    queryKey: ["verify-email", token],
    queryFn: () => {
      if (!token) {
        throw new Error("Kein Verifizierungs-Token vorhanden");
      }
      return backendApi.verifyEmail(token);
    },
    enabled: !!token,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

// ==========================================
// ✅ Login (useMutation)
// ==========================================
export const useLogin = () => {
  const queryClient = useQueryClient();

  return useMutation<
    LoginResponse,
    Error,
    { loginIdentifier: string; password: string }
  >({
    mutationFn: ({ loginIdentifier, password }) =>
      backendApi.login(loginIdentifier, password),
    // Sicherheitsrelevant: Vor dem Betreten der App den kompletten Cache leeren,
    // damit KEINE Daten (Feature-Flags, User, Onboarding, ...) einer vorherigen
    // Session sichtbar sind – auch nicht fuer einen Bruchteil einer Sekunde.
    // Hook-Level-Callbacks laufen VOR den mutate()-Callbacks (z.B. navigate).
    onSuccess: async () => {
      await queryClient.cancelQueries();
      queryClient.clear();
    },
  });
};

// ==========================================
// ✅ Aktueller User (useQuery)
// ==========================================
export const useCurrentUser = (enabled: boolean = true) => {
  return useQuery<
    {
      userName: string;
      userId: string;
      displayName: string;
      email: string;
    },
    Error
  >({
    queryKey: ["current-user"],
    queryFn: () => backendApi.me(),
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

// ==========================================
// ✅ Logout (useMutation)
// ==========================================
export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation<{ message: string }, Error>({
    mutationFn: () => backendApi.logout(),
    // Sowohl bei Erfolg als auch bei Fehler alle benutzerbezogenen Daten verwerfen.
    onSettled: async () => {
      await queryClient.cancelQueries();
      queryClient.clear();
    },
  });
};